/**
 * MapDroppedPinsNote Component
 * The chrome note for pins the parser dropped at the per-block cap.
 *
 * A block that named more places than the cap allows is still drawn, but the
 * reader is told what is missing — a map that silently shows 12 of 20 places is
 * a map that lies.
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';

const NOTE_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 11,
  padding: '4px 9px',
  borderRadius: 5,
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
  color: 'rgba(255,255,255,0.6)',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const DROPPED_PIN_NOUN = 'place';
const DROPPED_PINS_NOUN = 'places';

export interface MapDroppedPinsNoteProps {
  dataElScope: DataElScope;
  droppedPinCount: number;
}

export function MapDroppedPinsNote({
  dataElScope,
  droppedPinCount,
}: MapDroppedPinsNoteProps): ReactElement | null {

  if (droppedPinCount <= 0) {
    return null;
  }

  const noun = droppedPinCount === 1 ? DROPPED_PIN_NOUN : DROPPED_PINS_NOUN;

  return (
      <span
        data-el={dataEl(dataElScope, 'MapDroppedPinsNote/Root')}
        style={NOTE_STYLE}
        title={`This block named more places than one map shows, so ${droppedPinCount} ${noun} were left off.`}
      >
        {`${droppedPinCount} ${noun} not shown`}
      </span>
  );
}
